import { NextPage } from "next";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";

import { RhButton, RhInputFormik, RhLabel, RhToast } from "@rhythm-ui/react";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import * as Yup from "yup";

import { PhoneType } from "@/types/utils";

import api from "@/services/api";

import { LOCAL_STORAGE } from "@/constants";

import PhoneFormik from "@/components/formikInput/PhoneFormik";
import EntryLayout from "@/components/layout/EntryLayout";
import { Button } from "@/components/ui/Button";

const DemoRequestPage: NextPage = () => {
  return (
    <>
      <Head>
        <title>Schedule A Demo | Zelevate</title>
      </Head>

      <EntryLayout
        formPanel={<FormPanel />}
        greetingTitle={
          "Elevate Your Hiring Game with Pre-Interviewed Candidates"
        }
        greetingSubTitle={`Our Pre-Interviewed candidates have already gone through a
    rigorous evaluation process, so you can be confident that you
    are getting the best of the best. With Zelevate, you can
    streamline your hiring process and focus on what really matters
    – growing your business`}
      />
    </>
  );
};

export default DemoRequestPage;

const FormPanel = () => {
  const formNameRef = useRef<null | HTMLInputElement>(null);

  useEffect(() => {
    if (formNameRef.current) formNameRef.current.focus();
  }, []);

  const [showThankYouMessage, setShowThankYouMessage] = useState(false);

  const [initialForm, setInitialForm] = useState({
    name: "",
    mobile__form_value: {
      dialCode: "+91",
      phoneNumber: "",
    },
    email: "",
    recaptcha: "",
  });

  // read for email in localstorage; this is in case the user has been
  // redirected from landing page and filling just email
  // in that case read the email on localstorage and prefill in the form
  useEffect(() => {
    const userEmailFilledBeforeRedirect = localStorage.getItem(
      LOCAL_STORAGE.EMPLOYER_SIGNUP_EMAIL,
    );
    if (userEmailFilledBeforeRedirect) {
      setInitialForm((state) => ({
        ...state,
        email: userEmailFilledBeforeRedirect,
      }));
      localStorage.removeItem(LOCAL_STORAGE.EMPLOYER_SIGNUP_EMAIL);
    }
  }, []);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, "Too Short!")
      .max(100, "Too Long!")
      .required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    mobile__form_value: Yup.object().shape({
      dialCode: Yup.string(),
      phoneNumber: Yup.string()
        .typeError("Phone number is invalid.")
        .required("Phone number is required")
        .matches(/^\d+$/, "Phone number is invalid"),
      // .length(10, "Number must be 10 digit long"),
    }),
  });

  // captcha token state & ref to reset if needed
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<any>(null);

  const formSubmitHandler = async (
    formData: {
      name: string;
      mobile__form_value: PhoneType;
      email: string;
    },
    token: string,
  ) => {
    try {
      await api.company.postRequestDemo({
        name: formData.name,
        email: formData.email,
        mobile:
          formData.mobile__form_value.dialCode +
          formData.mobile__form_value.phoneNumber,
        recaptcha: token,
      });
      setInitialForm({
        name: "",
        mobile__form_value: {
          dialCode: "+91",
          phoneNumber: "",
        },
        email: "",
        recaptcha: "",
      });
      setShowThankYouMessage(true);
      setCaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.error ||
        "Something went wrong. We are working on it";
      RhToast.error(errMsg);
      setCaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  };
  const router = useRouter();
  return (
    <>
      {!showThankYouMessage ? (
        <>
          <h1>
            Schedule A<span className="zeal-text-gradient"> Demo</span>
          </h1>
          <div className="mb-txl lg:mb-tnano" />
          <Formik
            enableReinitialize
            initialValues={initialForm}
            onSubmit={async (values, { setSubmitting, setFieldError }) => {
              if (!captchaToken) {
                setFieldError("recaptcha", "Please complete the CAPTCHA.");
                setSubmitting(false);
                return;
              }

              try {
                await formSubmitHandler(values, captchaToken);
              } finally {
                setSubmitting(false);
              }
            }}
            validationSchema={validationSchema}
          >
            {({ values, setFieldValue, errors }) => (
              <Form>
                <RhInputFormik
                  name="name"
                  label="Name"
                  block
                  ref={formNameRef}
                />
                <RhLabel required>Phone Number</RhLabel>
                <PhoneFormik
                  valueKey={"mobile__form_value"}
                  values={values}
                  setFormikFieldValue={setFieldValue}
                />
                <RhInputFormik
                  block
                  name="email"
                  type="email"
                  label="Business Email"
                />
                {/* reCAPTCHA v2 checkbox */}
                <div className="my-4 flex">
                  <div className="inline-block">
                    <ReCAPTCHA
                      sitekey={
                        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string
                      }
                      onChange={(token) => {
                        setCaptchaToken(token);
                        setFieldValue("recaptcha", token);
                      }}
                      ref={recaptchaRef}
                    />

                    {errors.recaptcha && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.recaptcha}
                      </p>
                    )}
                  </div>
                </div>
                <RhButton className="my-2" type="submit" block>
                  Schedule My Demo
                </RhButton>
              </Form>
            )}
          </Formik>
        </>
      ) : (
        <div className="mx-auto grid max-w-[570px] gap-tm2">
          <Image
            src={"/icons/checkFilled.svg"}
            alt="check filled"
            width={100}
            height={100}
          />
          <h3 className="text-center">
            Thank you for your interest in Zelevate! We&apos;ll be reaching out
            shortly to schedule a demo
          </h3>

          <Button
            onClick={() => router.push("/employer")}
            className="mx-auto max-w-fit"
          >
            Return to Homepage
          </Button>
        </div>
      )}
    </>
  );
};

export async function getStaticProps() {
  return {
    props: {},
  };
}
